// src/services/marketAnalysisService.js
class MarketAnalysisService {
  constructor() {
    this.priceHistory = new Map(); // Symbol -> Array of price points
    this.volumeHistory = new Map(); // Symbol -> Array of volume points
    this.movingAverages = new Map(); // Symbol -> {MA20, MA50, MA200}
    this.supportResistanceLevels = new Map(); // Symbol -> {support: [], resistance: []}
    this.eventThresholds = {
      VOLUME_SPIKE: 2.5, // 250% increase from average
      MOMENTUM_SHIFT: 3.0, // 3% price change in 5 minutes
      SUPPORT_BREAK: 1.5, // 1.5% break from support/resistance
      PRICE_HISTORY_LIMIT: 1000, // Keep last 1000 price points
      EVENT_DEBOUNCE_TIME: 300000, // 5 minutes between similar events
    };
    this.lastEventTimes = new Map(); // EventType-Symbol -> timestamp
  }

  // Add new price data point
  addPricePoint(symbol, price, timestamp, volume) {
    if (!this.priceHistory.has(symbol)) {
      this.priceHistory.set(symbol, []);
      this.volumeHistory.set(symbol, []);
    }

    const pricePoints = this.priceHistory.get(symbol);
    const volumePoints = this.volumeHistory.get(symbol);

    // Add new data point
    pricePoints.push({ price, timestamp });
    volumePoints.push({ volume, timestamp });

    // Maintain history limit
    if (pricePoints.length > this.eventThresholds.PRICE_HISTORY_LIMIT) {
      pricePoints.shift();
      volumePoints.shift();
    }

    // Update analysis
    this.updateMovingAverages(symbol);
    this.updateSupportResistanceLevels(symbol);
  }

  // Calculate moving averages
  updateMovingAverages(symbol) {
    const prices = this.priceHistory.get(symbol);
    if (!prices || prices.length < 20) return;

    const calculateMA = (period) => {
      const relevantPrices = prices.slice(-period);
      const sum = relevantPrices.reduce((acc, point) => acc + point.price, 0);
      return sum / period;
    };

    this.movingAverages.set(symbol, {
      MA20: calculateMA(20),
      MA50: calculateMA(Math.min(50, prices.length)),
      MA200: calculateMA(Math.min(200, prices.length)),
    });
  }

  // Update support and resistance levels
  updateSupportResistanceLevels(symbol) {
    const prices = this.priceHistory.get(symbol);
    if (!prices || prices.length < 50) return;

    const recentPrices = prices.slice(-50);
    const priceValues = recentPrices.map((p) => p.price);

    // Simple algorithm to find local minima and maxima
    const levels = {
      support: [],
      resistance: [],
    };

    for (let i = 1; i < priceValues.length - 1; i++) {
      if (
        priceValues[i] < priceValues[i - 1] &&
        priceValues[i] < priceValues[i + 1]
      ) {
        levels.support.push(priceValues[i]);
      }
      if (
        priceValues[i] > priceValues[i - 1] &&
        priceValues[i] > priceValues[i + 1]
      ) {
        levels.resistance.push(priceValues[i]);
      }
    }

    // Keep only significant levels (remove clusters)
    levels.support = this.filterSignificantLevels(levels.support);
    levels.resistance = this.filterSignificantLevels(levels.resistance);

    this.supportResistanceLevels.set(symbol, levels);
  }

  // Filter out clustered price levels
  filterSignificantLevels(levels) {
    const significantLevels = [];
    const threshold = 0.005; // 0.5% difference for significance

    levels.sort((a, b) => a - b);

    for (let level of levels) {
      if (
        !significantLevels.some(
          (sig) => Math.abs((level - sig) / sig) < threshold
        )
      ) {
        significantLevels.push(level);
      }
    }

    return significantLevels.slice(-5); // Keep only last 5 significant levels
  }

  // Check for volume spikes
  detectVolumeSpike(symbol, currentVolume) {
    const volumes = this.volumeHistory.get(symbol);
    if (!volumes || volumes.length < 10) return false;

    const recentVolumes = volumes.slice(-10);
    const avgVolume =
      recentVolumes.reduce((sum, v) => sum + v.volume, 0) /
      recentVolumes.length;

    return currentVolume > avgVolume * this.eventThresholds.VOLUME_SPIKE;
  }

  // Check for momentum shifts
  detectMomentumShift(symbol, currentPrice) {
    const prices = this.priceHistory.get(symbol);
    if (!prices || prices.length < 10) return false;

    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    const recentPrices = prices.filter((p) => p.timestamp > fiveMinutesAgo);

    if (recentPrices.length < 2) return false;

    const priceChange =
      ((currentPrice - recentPrices[0].price) / recentPrices[0].price) * 100;
    return Math.abs(priceChange) >= this.eventThresholds.MOMENTUM_SHIFT;
  }

  // Check for support/resistance breaks
  detectSupportResistanceBreak(symbol, currentPrice) {
    const levels = this.supportResistanceLevels.get(symbol);
    if (!levels) return false;

    const { support, resistance } = levels;

    for (let level of [...support, ...resistance]) {
      const deviation = Math.abs(((currentPrice - level) / level) * 100);
      if (deviation >= this.eventThresholds.SUPPORT_BREAK) {
        return {
          level,
          type: currentPrice > level ? "resistance" : "support",
        };
      }
    }

    return false;
  }

  // Event debouncing check
  canTriggerEvent(symbol, eventType) {
    const key = `${eventType}-${symbol}`;
    const lastTime = this.lastEventTimes.get(key) || 0;
    const currentTime = Date.now();

    if (currentTime - lastTime >= this.eventThresholds.EVENT_DEBOUNCE_TIME) {
      this.lastEventTimes.set(key, currentTime);
      return true;
    }

    return false;
  }

  // Get market metrics for a symbol
  getMarketMetrics(symbol) {
    return {
      movingAverages: this.movingAverages.get(symbol) || {},
      supportResistance: this.supportResistanceLevels.get(symbol) || {},
      priceHistory: this.priceHistory.get(symbol) || [],
      volumeHistory: this.volumeHistory.get(symbol) || [],
    };
  }
}

export const marketAnalysisService = new MarketAnalysisService();
export default marketAnalysisService;
